/// <amd-module name="Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesReducer" />
import { default as produce } from "immer";
import { TestHubRefreshActions, TestHubRefreshActionsTypes } from "../TestHubRefresh/TestHubRefreshActions";
import { SuitesActions, SuitesActionsTypes } from "./SuitesActions";
import { ISuitesState, TestSuiteWithCount, ISuitesTreeFilteredState, } from "./SuitesContract";
import { TestSuite } from "TestPlanCommon/Contracts/TestPlan";
import { caseInsensitiveContains } from "VSS/Core/Util/String";

export function SuitesReducer(state: ISuitesState, action: SuitesActions | TestHubRefreshActions): ISuitesState {
    state = state || ({} as ISuitesState);

    return produce(state, (draftState: ISuitesTreeFilteredState) => {
        switch (action.type) {
            case SuitesActionsTypes.loadInitialTestDataSucceeded:
                draftState.testSuites = action.payload.testSuites;
                draftState.allTestSuites = action.payload.testSuites;
                draftState.idToTestSuiteMap = populateTestSuiteMap(action.payload.testSuites);
                draftState.idToTreeItemExpandableStateMap = new Map();
                draftState.editableSuiteId = -1;
                draftState.idToTreeItemExpandableStateMap = populateTreeItemStateMap(
                    action.payload.testSuites[0].id,
                    action.payload.selectedSuiteId,
                    draftState.idToTestSuiteMap
                );
                draftState.testSuiteExpandedStateMap = draftState.idToTreeItemExpandableStateMap;
                break;

            case SuitesActionsTypes.updateTestSuiteName:
                draftState.editableSuiteId = -1;
                const suites: TestSuiteWithCount = draftState.idToTestSuiteMap.get(action.payload.testSuiteId) as TestSuiteWithCount;
                suites.name = action.payload.testSuiteTitle;
                draftState.testSuites = [state.idToTestSuiteMap.get(action.payload.rootSuiteId) as TestSuiteWithCount];
                break;

            case SuitesActionsTypes.loadNewSuites:
                draftState.editableSuiteId = action.payload.editableSuiteId;
                for (let i = 0; i < action.payload.testSuites.length; i++) {
                    const parentSuites: TestSuiteWithCount = draftState.idToTestSuiteMap.get(action.payload.parentSuiteId) as TestSuiteWithCount;
                    if (!parentSuites.hasChildren) {
                        parentSuites.hasChildren = true;
                        parentSuites.children = [];
                    }

                    parentSuites.children.unshift(action.payload.testSuites[i]);
                    draftState.idToTestSuiteMap.set(action.payload.testSuites[i].id, action.payload.testSuites[i]);

                    draftState.idToTreeItemExpandableStateMap.set(action.payload.parentSuiteId, true);
                    draftState.testSuites = [state.idToTestSuiteMap.get(action.payload.rootSuiteId) as TestSuiteWithCount];
                }
                break;

            case SuitesActionsTypes.removeSuite:
                const suiteRemoved = state.idToTestSuiteMap.get(action.payload.suiteId) as TestSuiteWithCount;
                let parentSuite: TestSuiteWithCount = draftState.idToTestSuiteMap.get(suiteRemoved.parentSuite.id) as TestSuiteWithCount;
                let descendentSuiteIds: number[] = [];
                // Get descendent suites of the suites being removed
                descendentSuiteIds = getDescendentSuiteIds(suiteRemoved);

                for (let i = 0; i < parentSuite.children.length; i++) {
                    if (parentSuite.children[i].id === action.payload.suiteId) {
                        // Remove the TestSuite structure for the suite removed. Use the map to get the parent structure for quick computation.
                        parentSuite.children.splice(i, 1);
                        break;
                    }
                }
                // Set the parent expanded state to be false if no more children are present after the suite is removed
                if (parentSuite.children.length == 0) {
                    parentSuite.hasChildren = false;
                    draftState.idToTreeItemExpandableStateMap.set(parentSuite.id, false);
                }
                // Remove entries from the maps
                draftState.idToTestSuiteMap.delete(action.payload.suiteId);
                draftState.idToTreeItemExpandableStateMap.delete(action.payload.suiteId);

                const length: number = descendentSuiteIds.length;
                // Remove entries from the maps for allm descendent suites, as they will also get removed when parent suite is deleted
                for (let i = 0; i < length; i++) {
                    draftState.idToTestSuiteMap.delete(descendentSuiteIds[i]);
                    draftState.idToTreeItemExpandableStateMap.delete(descendentSuiteIds[i]);
                }
                draftState.testSuites = [state.idToTestSuiteMap.get(action.payload.rootSuiteId) as TestSuiteWithCount];
                break;

            case SuitesActionsTypes.showSuitePointsCount:
                const pointsCount = action.payload.suitePointCount;
                const rootSuiteId = state.testSuites[0].id;
                draftState.idToTestSuiteMap.forEach((value: TestSuiteWithCount, key: number) => {
                    value.pointCount = 0;
                    value.totalPointCount = 0;
                });
                for (let i = 0; i < pointsCount.length; i++) {
                    const suite = draftState.idToTestSuiteMap.get(pointsCount[i].testSuiteId) as TestSuiteWithCount;
                    if (suite) {
                        suite.pointCount = pointsCount[i].count;
                        suite.totalPointCount = pointsCount[i].totalCount;
                    }
                }

                if (action.payload.showTestsFromChildSuitesEnabled) {
                    /**
                     * Since idToTestSuiteMap will be updated with the recursive test point counts, we require a cloned map to find the original 
                     * test points count for every suite
                     */
                    let clonedMap = new Map(JSON.parse(JSON.stringify(Array.from(draftState.idToTestSuiteMap)))) as Map<number, TestSuiteWithCount>;

                    for (let i = 0; i < pointsCount.length; i++) {
                        const suite = draftState.idToTestSuiteMap.get(pointsCount[i].testSuiteId) as TestSuiteWithCount;
                        if(suite) {
                            let countObj = getRecursiveTestPointCount(suite, clonedMap);
                            if (countObj) {
                                suite.pointCount = countObj.pointCount;
                                suite.totalPointCount = countObj.totalPointCount;
                            }
                        }
                    }
                    /**
                     * suitePointCount array in payload contains only the suiteIds which has atleast 1 test point. But with showTestsFromChildSuites 
                     * option enabled, recursive test point count can be more than 0 and we need to calculate recursive count for those suites as well.
                     */
                    draftState.idToTestSuiteMap.forEach((value: TestSuiteWithCount, key: number) => {
                        if (value.totalPointCount === 0) {
                            let countObj = getRecursiveTestPointCount(value, clonedMap);
                            if (countObj) {
                                value.pointCount = countObj.pointCount;
                                value.totalPointCount = countObj.totalPointCount;
                            }
                        }
                    });
                }
                draftState.testSuites = [state.idToTestSuiteMap.get(rootSuiteId) as TestSuiteWithCount];
                break;

            case SuitesActionsTypes.showSuiteTestCasesCount:
                const testCasesCount = action.payload.suiteTestCasesCount;
                const rootId = state.testSuites[0].id;
                draftState.idToTestSuiteMap.forEach((value: TestSuiteWithCount, key: number) => {
                    value.testCaseCount = 0;
                    value.totalTestCaseCount = 0;
                });
                for (let i = 0; i < testCasesCount.length; i++) {
                    const suite = draftState.idToTestSuiteMap.get(testCasesCount[i].testSuiteId) as TestSuiteWithCount;
                    if (suite) {
                        suite.testCaseCount = testCasesCount[i].count;
                        suite.totalTestCaseCount = testCasesCount[i].totalCount;
                    }
                }
                draftState.testSuites = [state.idToTestSuiteMap.get(rootId) as TestSuiteWithCount];
                break;

            case SuitesActionsTypes.changeTreeItemExpandableState:
                draftState.idToTreeItemExpandableStateMap.set(action.payload.suiteId, action.payload.expandedState);
                break;

            case SuitesActionsTypes.editTestSuiteName:
                draftState.editableSuiteId = action.payload.suiteId;
                break;

            case SuitesActionsTypes.loadTestSuites:
                draftState.testSuites = action.payload.testSuites;
                draftState.idToTestSuiteMap = populateTestSuiteMap(action.payload.testSuites);
                draftState.idToTreeItemExpandableStateMap.set(action.payload.testSuites[0].id, true);
                break;

            case TestHubRefreshActionsTypes.updateTestPlan:
                const suiteId = state.testSuites[0].id;
                const rootSuite: TestSuiteWithCount = draftState.idToTestSuiteMap.get(suiteId) as TestSuiteWithCount;
                rootSuite.name = action.payload.testPlanName;
                draftState.testSuites = [state.idToTestSuiteMap.get(suiteId) as TestSuiteWithCount];
                break;

            case SuitesActionsTypes.queryEdited:
                draftState.editableSuiteId = -1;
                const testSuite = draftState.idToTestSuiteMap.get(action.payload.suiteId) as TestSuiteWithCount;
                testSuite.queryString = action.payload.queryText;
                draftState.testSuites = [state.idToTestSuiteMap.get(state.testSuites[0].id) as TestSuiteWithCount];
                break;

            case SuitesActionsTypes.reorderTestSuiteSucceeded:
                let draggedTestSuite = draftState.idToTestSuiteMap.get(action.payload.suiteId) as TestSuiteWithCount;
                let parentTestSuite = draftState.idToTestSuiteMap.get(draggedTestSuite.parentSuite.id) as TestSuiteWithCount;
                // Reorder the children order inside the parent test suite
                for (let i = 0; i < parentTestSuite.children.length; i++) {
                    if (parentTestSuite.children[i].id === action.payload.targetSuiteId) {
                        if (action.payload.dropAtEnd) {
                            parentTestSuite.children.splice(i + 1, 0, draggedTestSuite);
                        } else {
                            parentTestSuite.children.splice(i, 0, draggedTestSuite);
                        }
                        i++;
                    } else if (parentTestSuite.children[i].id === action.payload.suiteId) {
                        parentTestSuite.children.splice(i, 1);
                        i--;
                    }
                }
                draftState.testSuites = [state.idToTestSuiteMap.get(state.testSuites[0].id) as TestSuiteWithCount];
                break;

            case SuitesActionsTypes.updateTestSuiteParentSucceeded:
                let draggedSuite = draftState.idToTestSuiteMap.get(action.payload.suiteId) as TestSuiteWithCount;
                let sourceParentSuite: TestSuiteWithCount = draftState.idToTestSuiteMap.get(draggedSuite.parentSuite.id) as TestSuiteWithCount;
                for (let i = 0; i < sourceParentSuite.children.length; i++) {
                    if (sourceParentSuite.children[i].id === action.payload.suiteId) {
                        // Remove the TestSuite structure for the reordered suite from the source location.
                        sourceParentSuite.children.splice(i, 1);
                        break;
                    }
                }
                // Set the parent expanded state to be false if no more children are present after the suite is reordered
                if (sourceParentSuite.children.length == 0) {
                    sourceParentSuite.hasChildren = false;
                    draftState.idToTreeItemExpandableStateMap.set(sourceParentSuite.id, false);
                }

                let targetParentSuite: TestSuiteWithCount = draftState.idToTestSuiteMap.get(action.payload.targetParentSuiteId) as TestSuiteWithCount;
                if (targetParentSuite.hasChildren == undefined || targetParentSuite.hasChildren == false) {
                    targetParentSuite.hasChildren = true;
                    targetParentSuite.children = [];
                }
                draggedSuite.parentSuite.id = targetParentSuite.id;
                draggedSuite.parentSuite.name = targetParentSuite.name;
                // add the suite at the beginning of the child array for targetParentSuite
                targetParentSuite.children.unshift(draggedSuite);
                draftState.idToTreeItemExpandableStateMap.set(targetParentSuite.id, true);
                draftState.testSuites = [state.idToTestSuiteMap.get(state.testSuites[0].id) as TestSuiteWithCount];
                break;

            case SuitesActionsTypes.expandCollapseAllSuites:
                draftState.idToTreeItemExpandableStateMap = expandOrCollapseAll(
                    draftState.testSuites,
                    new Map<number, boolean>(),
                    action.payload.expandCollapseBool
                );
                draftState.testSuiteExpandedStateMap = draftState.idToTreeItemExpandableStateMap; 
                break;

            case SuitesActionsTypes.filterTestSuites:
                draftState.testSuites = draftState.allTestSuites;
                if (action.payload.keyword.length === 0) {
                    draftState.idToTreeItemExpandableStateMap = draftState.testSuiteExpandedStateMap;
                }
                else {
                    draftState.testSuites = filterSuitesByKeyword(
                        draftState.testSuites,
                        action.payload.keyword
                    );
                    draftState.idToTreeItemExpandableStateMap = expandOrCollapseAll(
                        draftState.testSuites,
                        new Map<number, boolean>(),
                        true
                    );
                }
                if(action.payload.callback){
                    action.payload.callback();
                }
                break;

            default:
                break;
        }        
    });
}

function getDescendentSuiteIds(suites: TestSuiteWithCount): number[] {
    return getDescendentSuiteIdsUtil(suites.children);
}

function getDescendentSuiteIdsUtil(suites: TestSuiteWithCount[]): number[] {
    const suiteIds: number[] = [];
    if (suites) {
        const suitesLength = suites.length;
        for (let i = 0; i < suitesLength; i++) {
            suiteIds.push(suites[i].id);
            const childIds: number[] = getDescendentSuiteIdsUtil(suites[i].children);
            suiteIds.push(...childIds);
        }
    }
    return suiteIds;
}

function populateTreeItemStateMap(rootSuiteId: number, selectedSuiteId: number, testSuiteMap: Map<number, TestSuiteWithCount>): Map<number, boolean> {
    const treeItemStateMap = new Map<number, boolean>();
    const parentIds = [];
    // root suite always expanded on page reload
    treeItemStateMap.set(rootSuiteId, true);
    // finding all the ancestors for selected suite
    while (selectedSuiteId !== rootSuiteId) {
        const parentTestSuite = testSuiteMap.get(selectedSuiteId) as TestSuiteWithCount;
        if (parentTestSuite) {
            const parentId = parentTestSuite.parentSuite.id;
            parentIds.unshift(parentId);
            selectedSuiteId = parentId;
        } else {
            break;
        }
    }
    // expanding tree item state for all the ancestors
    for (let i = 0; i < parentIds.length; i++) {
        treeItemStateMap.set(parentIds[i], true);
    }
    return treeItemStateMap;
}

function expandOrCollapseAll(
    suites: TestSuiteWithCount[],
    expandableStateMap: Map<number, boolean>,
    expandCollapseBool: boolean
): Map<number, boolean> {
    const suitesLength = suites.length;
    for (let i = 0; i < suitesLength; i++) {
        if (suites[i].children && suites[i].children.length > 0) {
            expandableStateMap.set(suites[i].id, expandCollapseBool);
            expandOrCollapseAll(suites[i].children, expandableStateMap, expandCollapseBool);
        }
    }
    return expandableStateMap;
}

function populateTestSuiteMap(suites: TestSuiteWithCount[], map?: Map<number, TestSuiteWithCount>): Map<number, TestSuiteWithCount> {
    // We only create a new map object if a reference to an existing object was not passed.
    if (!map) {
        map = new Map<number, TestSuiteWithCount>();
    }

    const suitesLength = suites.length;

    for (let i = 0; i < suitesLength; i++) {
        if (suites[i].hasChildren) {
            // We add the children to the existing map object.
            populateTestSuiteMap(suites[i].children, map);
        }
        map.set(suites[i].id, suites[i]);
    }
    return map;
}

function getRecursiveTestPointCount(
    suite: TestSuiteWithCount,
    idToTestSuiteMap: Map<number, TestSuiteWithCount>
): { pointCount: number, totalPointCount: number } {

    if(!suite) {
        return {
            pointCount: 0,
            totalPointCount: 0
        };
    }

    let countObj = {
        pointCount: suite.pointCount ? suite.pointCount : 0,
        totalPointCount: suite.totalPointCount ? suite.totalPointCount : 0
    };
    for (let i = 0; suite.children && i < suite.children.length; i++) {
        const childSuiteId = suite.children[i].id;
        const childSuite = idToTestSuiteMap.get(childSuiteId) as TestSuiteWithCount;
        const testPointCount = getRecursiveTestPointCount(childSuite, idToTestSuiteMap);
        countObj.pointCount += testPointCount.pointCount;
        countObj.totalPointCount += testPointCount.totalPointCount;
    }
    return countObj;
}

function filterSuitesByKeywordHelper(parentSuite:TestSuite, keyword: string):TestSuite|null {

    const allChildSuitesWithKeyword : TestSuite[] = [];
    if (parentSuite.hasChildren) {
        for (const child of parentSuite.children) {
            const childSuiteWithKeyword = filterSuitesByKeywordHelper(child, keyword);
            if (childSuiteWithKeyword) {
                allChildSuitesWithKeyword.push(childSuiteWithKeyword);
            }
        }
    }
    if (caseInsensitiveContains(parentSuite.name, keyword) || allChildSuitesWithKeyword.length > 0) {
        return {
            ...parentSuite,
            children: allChildSuitesWithKeyword,
            hasChildren: allChildSuitesWithKeyword.length>0
        };
    }
    return null;
}
function filterSuitesByKeyword(allSuites: TestSuiteWithCount[], keyword: string): TestSuiteWithCount[] {

    let filteredSuites: TestSuiteWithCount[] = [];
    for (const suite of allSuites) {
        const filterSuiteSubTree = filterSuitesByKeywordHelper(suite, keyword);
        if (filterSuiteSubTree) {
            filteredSuites.push(filterSuiteSubTree);
        }
    }
    return filteredSuites;
}